/**
 * D3 (Himaad UI feedback, 2026-08-27) — the ONE gate deciding whether a report
 * column's header earns its "i" info icon.
 *
 * Kept in a plain module, no React component, because it is called from BOTH grid
 * component files and a helper exported from one of those trips
 * react-refresh/only-export-components (cb2 CLAUDE.md: "put shared helpers in a
 * `*.js` util" — same split as queueFormat.js / autoReportColumns.js). It also
 * keeps `common/DataGrid.jsx` from having to import the grouped grid, or vice
 * versa, to share a condition.
 *
 * WHY THERE IS A GATE AT ALL. `columnDefinition`
 * (src/components/reports/reportGridUtils.js:697-736) does not describe a column:
 * it restates the column's TYPE and then appends the grid's generic affordance
 * hint. Measured on the live catalog, the icon lit on effectively 100% of 958 grid
 * columns and said one of only SEVEN auto-generated sentences — "Text.",
 * "Number.", "Dropdown.", "Tags.", "Date.", "Lookup.", "Number, summed in the
 * report total." (the type words are FILTER_TYPE_LABEL, reportGridUtils.js:172) —
 * each followed by "Click to sort; type to filter.". An icon on every column
 * carries no information, which is exactly what Himaad meant by "not needed on
 * every column".
 *
 * Jordan's decision (2026-08-27): SUPPRESS THE FILLER NOW, author real per-column
 * definitions later (the stored-description model is the CC-49420 follow-up). So
 * as of today this suppresses the icon on every report column — `columnDefinition`
 * has exactly two return shapes, '' and filler — and the icon comes back on its
 * own, per column, the moment a definition arrives that is not filler. No page,
 * prop or call site has to be revisited for that to happen.
 *
 * THE TEST IS THE SUFFIX, not the seven sentences. `columnDefinition` appends the
 * hint to every string it builds (reportGridUtils.js:735) and nothing else in the
 * app produces that sentence, so keying off it cannot drift as the type list
 * grows — where a list of head sentences would silently stop matching and quietly
 * put the icon back on 958 columns.
 *
 * ⚠️ The corollary is a constraint on CC-49420: a real authored definition must
 * NOT be concatenated with `columnDefinition`'s hint, or this predicate will
 * suppress it too. Gate on the hint's absence; don't append the hint.
 *
 * NOTHING BUT THE ICON IS SUPPRESSED, and the hint's two claims stay reachable
 * wherever they are the accessible name of a real control — checked before
 * cutting, not assumed:
 *   - the sort control is named `aria-label="Sort by {column}"` in both grids
 *     (DataGrid.jsx's header button; ReportGroupedGrid.jsx's, which additionally
 *     keeps its own "Click to sort · Shift+click to add a secondary sort" title);
 *   - the per-column filter is named by ColumnValuePicker's `label={column
 *     label}` (flat: DataGrid.jsx's ColumnFilterCell; grouped: its own filter
 *     row).
 * Neither reads `meta.definition`, so no control loses its name here. The icon
 * itself is `role="img"`, not a control.
 */

/* The generic affordance hint `columnDefinition` appends to EVERY definition it
   builds (reportGridUtils.js:735). Nothing else produces this sentence, which is
   what makes it a reliable filler marker. */
const GENERIC_AFFORDANCE_HINT = 'Click to sort; type to filter.'

/**
 * Does this column definition say something a header label does not?
 *
 * @param definition the `columnDefinition` string (or an authored one, later)
 * @returns true only for a definition worth an icon
 */
export const isAuthoredDefinition = (definition) =>
  typeof definition === 'string' &&
  definition.trim() !== '' &&
  !definition.trimEnd().endsWith(GENERIC_AFFORDANCE_HINT)
